import { Injectable } from '@angular/core';
import { GlobalConfigService } from './global-config.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticationService } from '@app/services/authentication.service';
import { Role } from '@app/models/user';
import { UserGroupConfig } from '@app/models/user-group';
import { AdminAccessConfig } from '@app/models/global-config';
import { Route } from '@angular/router';

export interface PermissionConfig {
  account_create?: boolean;
  account_edit?: boolean;
  child_create?: boolean;
  child_edit?: boolean;
  preinscription_create?: boolean;
  preinscription_edit?: boolean;
  reservation_diabolo?: boolean;
  reservation_mikado?: boolean;
  foyer_edit?: boolean;
  lasido_create?: boolean;
  lasido_edit?: boolean;
  cogito_create?: boolean;
  cogito_edit?: boolean;
  cogito_inscriptions_create?: boolean;
  cogito_inscriptions_edit?: boolean;
  diabolo_inscriptions_create?: boolean;
  diabolo_inscriptions_edit?: boolean;
  informations_assmat_edit?: boolean;
  disponibilites_edit?: boolean;
  formations_edit?: boolean;
  enabled_assmat_account?: boolean;
  contrat_accueil_edit?: boolean;
  contrat_accueil_create?: boolean;
  animations_add?: boolean;
  reunions_add?: boolean;
  child_photo?: boolean;
  mandat_sepa_create?: boolean;
  mandat_sepa_edit?: boolean;
  mandat_sepa_display_foyer?: boolean;
  child_access?: boolean;
  messagerie_access?: boolean;
  messagerie_attachments_enabled?: boolean;
  document_access?: boolean;
  foyer_access?: boolean;
  mandat_sepa_access?: boolean;
  factures_access?: boolean;
  tickets_access?: boolean;
  editions_access?: boolean;
  planning_pointage_access?: boolean;
  animations_access?: boolean;
  reunions_access?: boolean;
  contrat_accueil_access?: boolean;
  demande_contrat_accueil_access?: boolean;
  demande_contrat_accueil_create?: boolean;
  demande_contrat_accueil_edit?: boolean;
  cogito_access?: boolean;
  cogito_inscriptions_access?: boolean;
  diabolo_inscriptions_access?: boolean;
  preinscription_access?: boolean;
  lasido_access?: boolean;
  lasido_renewal_inscriptions_access?: boolean;
  planning_resa_access?: boolean;
  enabled_enseignant_account?: boolean;
  enseignant_planning_pointage_access?: boolean;
  enseignant_evaluation_access?: boolean;
  // enseignant_evaluation_create?: boolean;
  document_add?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  permission$ = this.globalConfigService.getPart('permissions') as Observable<PermissionConfig>;
  adminAccess$ = this.globalConfigService.getPart('adminAccess') as Observable<AdminAccessConfig>;

  constructor(
    private authService: AuthenticationService,
    private globalConfigService: GlobalConfigService
  ) { }

  updatePermissions(perm) {
    return this.globalConfigService.updatePart('permissions', perm, true);
  }

  updateAdminAccess(userGroupConfig: UserGroupConfig[]) {
    return this.globalConfigService.updatePart('adminAccess', { config: userGroupConfig }, true);
  }

  getPermissionSettings() {
    return this.globalConfigService.settings.permissions;
  }

  hasPermission(perm: keyof PermissionConfig) {
    const permObject = this.getPermissionSettings();

    if (permObject && (perm in permObject)) {
      return permObject[perm];
    }

    return false;
  }

  hasAdminAccess(link: string, category: string): Observable<boolean> {
    const currentUser = this.authService.currentUserValue;
    const userGroups = currentUser?.groupes || [];

    return this.adminAccess$.pipe(
      map(adminAccess => {
        // Le lien 'access-control' est réservé à l'Admin(Superviseur) uniquement
        if (link === 'access-control' && currentUser?.role !== Role.Admin) {
          return false;
        }

        // Si l'utilisateur est Admin, il a accès à tout
        if (currentUser?.role === Role.Admin) return true;

        // Si l'utilisateur n'a pas de groupes, il a accès à tout
        if (!userGroups.length || !adminAccess) return true;

        // Normaliser la configuration pour gérer les deux formats possibles
        const configs = Array.isArray(adminAccess) ? adminAccess :
          adminAccess.config ? adminAccess.config : [];

        // Vérifie si l'utilisateur a accès dans au moins un de ses groupes
        return userGroups.some(userGroup => {

          const groupConfig = configs.find(g => g.id === userGroup.idGroupeUtilisateur.toString());

          if (!groupConfig) return true;

          // Vérifier d'abord si la catégorie a un accès global
          if (groupConfig.permissions?.[category]?.all === true) {
            return true;
          }

          // Sinon vérifier l'accès spécifique au lien
          return groupConfig.permissions?.[category]?.[link];
        });
      })
    );
  }

  isManager() {
    return this.authService.hasRole(Role.Manager);
  }
}
